'use client';

import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, AlertCircle, ShieldCheck, ArrowRight } from 'lucide-react';
import { Toaster } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';


interface LoginFormValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

type FormState = 'idle' | 'loading' | 'error' | 'success';

export default function AdminLoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [formState, setFormState] = useState<FormState>('idle');
  const [authError, setAuthError] = useState<string | null>(null);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<LoginFormValues>({
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
    mode: 'onTouched',
  });

  const emailValue = watch('email');

  const onSubmit = useCallback(async (data: LoginFormValues) => {
    setFormState('loading');
    setAuthError(null);

    try {
      const supabase = createClient();
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });
      if (error) throw error;

      if (authData.session) {
        setFormState('success');
        toast.success('Authenticated successfully', {
          description: 'Redirecting…',
          duration: 2000,
        });
        // Redirect cricketscore@antrayu.com to Match screen, stats@antrayu.com to Live Match Stats, others to dashboard
        const destination =
          data.email.toLowerCase() === 'cricketscore@antrayu.com' ? '/match' :
          data.email.toLowerCase() === 'stats@antrayu.com' ? '/live-match-stats' : '/dashboard';
        setTimeout(() => {
          window.location.href = destination;
        }, 800);
      }
    } catch (error: any) {
      setFormState('error');
      setAuthError(error?.message || 'Invalid credentials. Please try again.');
    }
  }, [router]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const isLoading = formState === 'loading';
  const isSuccess = formState === 'success';

  return (
    <>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: 'hsl(224 13% 13%)',
            border: '1px solid hsl(224 13% 20%)',
            color: 'hsl(220 14% 96%)',
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '14px',
          },
        }}
      />

      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck
              size={16}
              className="text-[hsl(var(--primary))]"
              aria-hidden="true"
            />
            <span className="text-[12px] font-medium text-[hsl(var(--primary))] tracking-wider uppercase">
              Secure Admin Access
            </span>
          </div>
          <h2 className="text-[26px] font-bold text-white tracking-tight leading-tight mb-2">
            Sign in to Control Panel
          </h2>
          <p className="text-[14px] text-zinc-400 leading-relaxed">
            Access your broadcasting dashboard, stream analytics, and broadcaster management tools.
          </p>
        </div>

        {/* Auth error banner */}
        {formState === 'error' && authError && (
          <div
            className="flex items-start gap-3 px-4 py-3 rounded-xl border border-red-500/20 bg-red-500/8 mb-6 animate-fade-in"
            role="alert"
            aria-live="assertive"
          >
            <AlertCircle
              size={16}
              className="text-red-400 flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div>
              <p className="text-[13px] font-semibold text-red-300 leading-snug mb-0.5">
                Authentication Failed
              </p>
              <p className="text-[12px] text-red-400/80 leading-relaxed break-all">
                {authError}
              </p>
            </div>
          </div>
        )}

        {/* Success banner */}
        {formState === 'success' && (
          <div
            className="flex items-start gap-3 px-4 py-3 rounded-xl border border-green-500/20 bg-green-500/8 mb-6 animate-fade-in"
            role="status"
            aria-live="polite"
          >
            <ShieldCheck
              size={16}
              className="text-green-400 flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div>
              <p className="text-[13px] font-semibold text-green-300 leading-snug mb-0.5">
                Identity Verified
              </p>
              <p className="text-[12px] text-green-400/80 leading-relaxed">
                Redirecting to Broadcasting Control Panel…
              </p>
            </div>
          </div>
        )}

        {/* Form */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="space-y-5"
          aria-label="Admin login form"
        >
          {/* Email field */}
          <div className="space-y-1.5">
            <label
              htmlFor="email"
              className="block text-[13px] font-medium text-zinc-300 tracking-wide"
            >
              Admin Email Address
            </label>
            <div className="relative">
              <div
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2"
                aria-hidden="true"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={
                    errors.email
                      ? 'text-red-400'
                      : emailValue
                      ? 'text-zinc-300' : 'text-zinc-500'
                  }
                >
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
              </div>
              <input
                id="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="admin@antrayu.com"
                disabled={isLoading || isSuccess}
                aria-invalid={errors.email ? 'true' : 'false'}
                aria-describedby={errors.email ? 'email-error' : undefined}
                className={`
                  w-full pl-10 pr-4 py-3 rounded-xl text-[14px] text-white placeholder:text-zinc-600
                  input-dark
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${errors.email
                    ? 'border-red-500/40 bg-red-500/5 focus:border-red-500/60 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.12)]'
                    : ''
                  }
                  transition-all duration-200
                `}
                {...register('email', {
                  required: 'Email address is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email address',
                  },
                })}
              />
            </div>
            {errors.email && (
              <p
                id="email-error"
                className="flex items-center gap-1.5 text-[12px] text-red-400 animate-fade-in"
                role="alert"
              >
                <AlertCircle size={11} aria-hidden="true" />
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Password field */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label
                htmlFor="password"
                className="block text-[13px] font-medium text-zinc-300 tracking-wide"
              >
                Password
              </label>
              <button
                type="button"
                className="text-[12px] text-zinc-500 hover:text-[hsl(var(--primary))] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]/50 rounded"
                tabIndex={0}
                aria-label="Forgot password — contact your system administrator"
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <div
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2"
                aria-hidden="true"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={errors.password ? 'text-red-400' : 'text-zinc-500'}
                >
                  <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Enter your password"
                disabled={isLoading || isSuccess}
                aria-invalid={errors.password ? 'true' : 'false'}
                aria-describedby={errors.password ? 'password-error' : undefined}
                className={`
                  w-full pl-10 pr-12 py-3 rounded-xl text-[14px] text-white placeholder:text-zinc-600
                  input-dark
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${errors.password
                    ? 'border-red-500/40 bg-red-500/5 focus:border-red-500/60 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.12)]'
                    : ''
                  }
                  transition-all duration-200
                `}
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters',
                  },
                })}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                disabled={isLoading || isSuccess}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]/50 rounded p-0.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {showPassword ? (
                  <EyeOff size={16} aria-hidden="true" />
                ) : (
                  <Eye size={16} aria-hidden="true" />
                )}
              </button>
            </div>
            {errors.password && (
              <p
                id="password-error"
                className="flex items-center gap-1.5 text-[12px] text-red-400 animate-fade-in"
                role="alert"
              >
                <AlertCircle size={11} aria-hidden="true" />
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Remember me */}
          <div className="flex items-center gap-2.5 pt-0.5">
            <div className="relative flex items-center">
              <input
                id="rememberMe"
                type="checkbox"
                disabled={isLoading || isSuccess}
                className="sr-only peer"
                {...register('rememberMe')}
              />
              <label
                htmlFor="rememberMe"
                className="
                  w-4 h-4 rounded flex-shrink-0 cursor-pointer
                  border border-white/10 bg-white/[0.04]
                  peer-checked:bg-[hsl(var(--primary))] peer-checked:border-[hsl(var(--primary))]
                  peer-focus-visible:ring-2 peer-focus-visible:ring-[hsl(var(--primary))]/40 peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-transparent
                  transition-all duration-150
                  flex items-center justify-center
                  peer-disabled:opacity-40 peer-disabled:cursor-not-allowed
                "
                aria-hidden="true"
              >
                <svg
                  width="9"
                  height="9"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="opacity-0 peer-checked:opacity-100 transition-opacity duration-100"
                  aria-hidden="true"
                >
                  <path d="M2 6l3 3 5-5" />
                </svg>
              </label>
            </div>
            <label
              htmlFor="rememberMe"
              className="text-[13px] text-zinc-400 cursor-pointer select-none hover:text-zinc-300 transition-colors duration-150"
            >
              Keep me signed in for 30 days
            </label>
          </div>

          {/* Submit button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isLoading || isSuccess}
              aria-label="Sign in to AntrayuLive Admin Control Panel"
              className="
                btn-primary
                w-full flex items-center justify-center gap-2.5
                px-6 py-3.5 rounded-xl
                text-[14px] font-semibold text-white
                focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
                disabled:transform-none
              "
            >
              {isLoading ? (
                <>
                  <Loader2
                    size={16}
                    className="animate-spin flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span>Verifying identity…</span>
                </>
              ) : isSuccess ? (
                <>
                  <ShieldCheck size={16} className="flex-shrink-0" aria-hidden="true" />
                  <span>Authenticated — Redirecting…</span>
                </>
              ) : (
                <>
                  <span>Access Control Panel</span>
                  <ArrowRight
                    size={15}
                    className="flex-shrink-0 transition-transform duration-200 group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                </>
              )}
            </button>
          </div>
        </form>

        {/* Footer */}
        <p className="mt-6 text-center text-[12px] text-zinc-600 leading-relaxed">
          By signing in, you agree to AntrayuLive's{' '}
          <button
            type="button"
            className="text-zinc-500 hover:text-zinc-300 transition-colors duration-150 underline underline-offset-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-zinc-400 rounded"
          >
            Terms of Service
          </button>{' '}
          and{' '}
          <button
            type="button"
            className="text-zinc-500 hover:text-zinc-300 transition-colors duration-150 underline underline-offset-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-zinc-400 rounded"
          >
            Privacy Policy
          </button>
          .
        </p>
      </div>
    </>
  );
}